import { defineStore } from 'pinia'
import { STORE_ID } from '~/constants/store'
import type { NavigationState as State } from '~/types/navigation'
import * as Sentry from '@sentry/capacitor'

const initialState = (): State => ({
  isInitialisingNavigation: {
    home: true,
    catering: true
  },
  pushFrom: undefined,
  pushTo: undefined,
  modalFrom: undefined,
  modalTo: undefined,
  promoId: undefined,
  restaurantId: undefined,
  productId: undefined,
  categoryId: undefined,
  orderId: undefined,
  temporaryRestaurantId: undefined,
  temporaryOrderType: undefined,
  referralCode: undefined,
  experienceTag: undefined,
  signUpExperience: undefined,
  suppressAutoLocationSearch: false,
  universalLinkTarget: undefined
})

/**
 * @name useNavigationStore
 * @description Store for navigation state across the app
 *
 */
const useNavigationStore = defineStore(STORE_ID.NAVIGATION, {
  state: initialState,

  getters: {
    willNavigateToProduct(): boolean {
      return this.pushTo === 'menu-product' && this.productId !== undefined
    },
    willNavigateToCategory(): boolean {
      return this.pushTo === '/menu' && this.categoryId !== undefined
    },
    willNavigateToOrderConfig(): boolean {
      return this.pushTo === '/config'
    },
    willNavigateToOrderHistory(): boolean {
      return this.pushTo === '/history'
    },
    willNavigateToRelish(): boolean {
      return this.pushTo === '/relish'
    },
    willNavigateToProfile(): boolean {
      return (
        this.pushTo === '/profile' ||
        this.pushTo === '/profile/mad-bunday' ||
        this.pushTo === '/profile/edit' ||
        this.pushTo === '/profile/edit/student-sign-up' ||
        this.pushTo === '/profile/referrals'
      )
    }
  },
  actions: {
    resetPushNavigation() {
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: `Resetting push navigation state`,
        level: 'info'
      })
      this.pushFrom = undefined
      this.pushTo = undefined
    },
    resetModalNavigation() {
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: `Resetting modal navigation state`,
        level: 'info'
      })
      this.modalFrom = undefined
      this.modalTo = undefined
      this.promoId = undefined
    },
    resetReferralCode() {
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: `Resetting referral code state`,
        level: 'info'
      })
      this.referralCode = undefined
    },
    resetSignUpExperience() {
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: `Resetting sign up experience state`,
        level: 'info'
      })
      this.experienceTag = undefined
      this.signUpExperience = undefined
    }
  },
  // TODO in future consider persisting some of the the navigation state to allow restoring the current
  // position in the app after reloading (e.g. after panel reopens)
  persist: true
})

export default useNavigationStore
export { initialState }
